
import React, { useEffect, useState } from 'react';
import StaffLayout from '../../layouts/StaffLayout';
import { Box, Typography, Grid, Paper, CircularProgress, Tooltip, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Button } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../services/api';

function SpeakerDetailModal({ open, onClose, speaker }) {
  if (!speaker) return null;
  const details = speaker.bookingDetails || {};
  const country = details.country || speaker.userId?.country || '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={theme => ({
        bgcolor: theme.palette.mode === 'dark' ? '#23243a' : '#f5f5fa',
        color: theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
        fontWeight: 700,
        fontSize: 20,
        textAlign: 'center',
        borderBottom: `1.5px solid ${theme.palette.divider}`
      })}>Speaker Details</DialogTitle>
      <DialogContent dividers sx={theme => ({
        bgcolor: theme.palette.mode === 'dark' ? '#23243a' : '#fafbff',
        color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
        p: 3
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {country && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.2em', height: '2.2em', borderRadius: '7px', boxShadow: '0 0 4px #1976d2', border: '2px solid #1976d2', background: '#fff', marginRight: 8 }}>
              <ReactCountryFlag countryCode={country} svg style={{ width: '2em', height: '2em', borderRadius: '7px', verticalAlign: 'middle' }} />
            </span>
          )}
          <Typography variant="h6" fontWeight={700} sx={theme => ({ color: theme.palette.mode === 'dark' ? '#fff' : '#1976d2', fontSize: 18 })}>
            {details.bookerName || details.companyDetails?.name || speaker.userId?.name || 'N/A'}
          </Typography>
        </Box>
        <Divider sx={{ my: 2, bgcolor: theme => theme.palette.mode === 'dark' ? '#444' : undefined }} />
        <Typography variant="body2" sx={theme => ({ fontWeight: 500, fontSize: 15, color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' })}>
          <b>Email:</b> {speaker.userId?.email || details.email || 'N/A'}
        </Typography>
        <Typography variant="body2" sx={theme => ({ fontWeight: 500, fontSize: 15, color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' })}>
          <b>Package:</b> {speaker.packageId?.name || 'N/A'} | <b>Category:</b> {speaker.packageId?.category || 'N/A'}
        </Typography>
        <Typography variant="body2" sx={theme => ({ fontWeight: 500, fontSize: 15, color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' })}>
          <b>Mobile:</b> {details.mobile || 'N/A'}
        </Typography>
        <Typography variant="body2" sx={theme => ({ fontWeight: 500, fontSize: 15, color: theme.palette.mode === 'dark' ? '#fff' : 'inherit' })}>
          <b>Country:</b> {country || 'N/A'}
        </Typography>
      </DialogContent>
      <DialogActions sx={theme => ({ bgcolor: theme.palette.mode === 'dark' ? '#23243a' : undefined })}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={theme => ({ color: theme.palette.mode === 'dark' ? '#fff' : undefined })}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function StaffSpeakersPage() {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSpeaker, setDetailSpeaker] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionSpeaker, setActionSpeaker] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/bookings/approved-speakers')
      .then(res => setSpeakers(Array.isArray(res.data) ? res.data : []))
      .catch(err => setError(err.response?.data?.error || 'Failed to fetch speakers'))
      .finally(() => setLoading(false));
  }, []);

  const handleViewDetail = (speaker) => {
    setDetailSpeaker(speaker);
    setDetailOpen(true);
  };

  const handleActionMenu = (event, speaker) => {
    setActionAnchorEl(event.currentTarget);
    setActionSpeaker(speaker);
  };

  const closeActionMenu = () => {
    setActionAnchorEl(null);
    setActionSpeaker(null);
  };

  return (
    <StaffLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Speaker Management
        </Typography>
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            All approved and paid speakers are listed below.
          </Typography>
          {error && <Typography color="error" mb={2}>{error}</Typography>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {speakers.length === 0 && (
                <Grid item xs={12}><Typography>No speakers found.</Typography></Grid>
              )}
              {speakers.map((s) => {
                const details = s.bookingDetails || {};
                const country = details.country || s.userId?.country || '';
                // Status icons
                const isApproved = s.status === 'approved';
                const isPending = s.status === 'pending';
                const isPaid = s.paymentStatus === 'paid' || s.paymentStatus === 'verified';
                return (
                  <Grid item xs={12} sm={6} md={4} key={s._id}>
                    <Paper sx={theme => ({
                      p: 2.2,
                      borderRadius: 3,
                      boxShadow: 3,
                      mb: 2,
                      background: theme.palette.mode === 'dark' ? '#23243a' : '#fcfcff',
                      border: `1.5px solid ${theme.palette.divider}`,
                      transition: 'background 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        background: theme.palette.mode === 'dark' ? '#28294a' : '#f5f7fa',
                      },
                    })}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Country flag styled as theme */}
                        {country && (
                          <span style={{ fontSize: 22, borderRadius: '7px', background: '#fff', boxShadow: '0 1px 4px #1976d2', padding: '2px 6px', border: '1.5px solid #1976d2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2em', height: '2em' }}>
                            <ReactCountryFlag countryCode={country} svg style={{ width: '1.5em', height: '1.5em', borderRadius: '7px', verticalAlign: 'middle' }} />
                          </span>
                        )}
                        <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ fontSize: 16.5 }}>
                          {details.bookerName || details.companyDetails?.name || s.userId?.name || 'N/A'}
                        </Typography>
                        {/* Status icons */}
                        {isApproved && <span title="Approved" style={{ color: '#1dbf73', marginLeft: 5, fontSize: 17, fontWeight: 700 }}>✔✔</span>}
                        {isPending && <span title="Pending" style={{ color: '#d32f2f', marginLeft: 5, fontSize: 17, fontWeight: 700 }}>✔</span>}
                        {isPaid && <span title="Paid" style={{ color: '#21c700', marginLeft: 5, fontSize: 17, fontWeight: 700 }}>$</span>}
                        {/* View detail icon */}
                        <Tooltip title="View Details">
                          <span>
                            <IconButton size="small" color="info" onClick={() => handleViewDetail(s)} sx={{ ml: 1 }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* Action menu */}
                        <Tooltip title="Actions">
                          <span>
                            <IconButton size="small" onClick={e => handleActionMenu(e, s)} sx={{ ml: 0.5 }}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: 13.5, mt: 0.2 }}>
                        <b>Email:</b> {s.userId?.email || details.email || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.2, fontSize: 13.2 }}>
                        <b>Package:</b> {s.packageId?.name || 'N/A'} | <b>Category:</b> {s.packageId?.category || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.2, fontSize: 13.2 }}>
                        <b>Mobile:</b> {details.mobile || 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
          <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={closeActionMenu}>
            <MenuItem onClick={() => { if (actionSpeaker) handleViewDetail(actionSpeaker); closeActionMenu(); }}>View Details</MenuItem>
            {/* Add more actions here as needed */}
          </Menu>
        </Paper>
        {/* Speaker Detail Modal */}
        <SpeakerDetailModal open={detailOpen} onClose={() => setDetailOpen(false)} speaker={detailSpeaker} />
      </Box>
    </StaffLayout>
  );
}

export default StaffSpeakersPage;
